import { ServiceStoreConfig } from "../types/ServiceStoreTypes";

/**
 * Scheduling Module Service Stores
 */
export const schedulingStores: ServiceStoreConfig[] = [
  {
    name: "form-capacity",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.formCapacityStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalForms = entities.length;
          const availableForms = entities.filter(
            (form: Record<string, unknown>) => {
              const capacity = (form.capacity as number) || 0;
              const allocated = (form.allocated as number) || 0;
              return capacity > allocated;
            }
          ).length;
          const fullCapacityForms = entities.filter(
            (form: Record<string, unknown>) => {
              const capacity = (form.capacity as number) || 0;
              const allocated = (form.allocated as number) || 0;
              return capacity <= allocated;
            }
          ).length;
          const totalCapacity = entities.reduce(
            (sum: number, form: Record<string, unknown>) => {
              return sum + ((form.capacity as number) || 0);
            },
            0
          );

          return [
            {
              title: "Total Forms",
              value: () => totalForms.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Available",
              value: () => availableForms.toString(),
              valueColor: "success.main",
            },
            {
              title: "At Capacity",
              value: () => fullCapacityForms.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Total Capacity",
              value: () => totalCapacity.toString(),
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "product-form-mappings",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.productFormMappingsStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalMappings = entities.length;
          const activeMappings = entities.filter(
            (mapping: Record<string, unknown>) =>
              mapping.status === "active" || mapping.isActive !== false
          ).length;
          const customMappings = entities.filter(
            (mapping: Record<string, unknown>) =>
              mapping.type === "custom" || mapping.isCustom
          ).length;
          const defaultMappings = entities.filter(
            (mapping: Record<string, unknown>) =>
              mapping.type === "default" || mapping.isDefault
          ).length;

          return [
            {
              title: "Total Mappings",
              value: () => totalMappings.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Active",
              value: () => activeMappings.toString(),
              valueColor: "success.main",
            },
            {
              title: "Custom",
              value: () => customMappings.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Default",
              value: () => defaultMappings.toString(),
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "production-schedules",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.productionSchedulesStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalSchedules = entities.length;
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const todaySchedules = entities.filter(
            (schedule: Record<string, unknown>) => {
              const scheduleDate = new Date(schedule.scheduleDate as string);
              scheduleDate.setHours(0, 0, 0, 0);
              return scheduleDate.getTime() === today.getTime();
            }
          ).length;
          const completedSchedules = entities.filter(
            (schedule: Record<string, unknown>) =>
              schedule.status === "completed"
          ).length;
          const pendingSchedules = entities.filter(
            (schedule: Record<string, unknown>) =>
              schedule.status === "pending" || schedule.status === "scheduled"
          ).length;

          return [
            {
              title: "Total Schedules",
              value: () => totalSchedules.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Today",
              value: () => todaySchedules.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Pending",
              value: () => pendingSchedules.toString(),
              valueColor: "info.main",
            },
            {
              title: "Completed",
              value: () => completedSchedules.toString(),
              valueColor: "success.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
        defaultSort: {
          sortBy: ["scheduleDate"],
          sortDirection: { scheduleDate: "ASC" },
        },
      },
    },
  },
  {
    name: "sales-orders",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.salesOrdersStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalOrders = entities.length;
          const pendingOrders = entities.filter(
            (order: Record<string, unknown>) =>
              order.status === "pending" || order.status === "new"
          ).length;
          const scheduledOrders = entities.filter(
            (order: Record<string, unknown>) => order.status === "scheduled"
          ).length;
          const completedOrders = entities.filter(
            (order: Record<string, unknown>) =>
              order.status === "completed" || order.status === "delivered"
          ).length;

          return [
            {
              title: "Total Orders",
              value: () => totalOrders.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Pending",
              value: () => pendingOrders.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Scheduled",
              value: () => scheduledOrders.toString(),
              valueColor: "info.main",
            },
            {
              title: "Completed",
              value: () => completedOrders.toString(),
              valueColor: "success.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "sales-order-lines",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.salesOrderLinesStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalLines = entities.length;
          const pendingLines = entities.filter(
            (line: Record<string, unknown>) =>
              line.status === "pending" || line.status === "new"
          ).length;
          const scheduledLines = entities.filter(
            (line: Record<string, unknown>) =>
              line.status === "scheduled" || line.isScheduled
          ).length;
          const totalQuantity = entities.reduce(
            (sum: number, line: Record<string, unknown>) => {
              return sum + ((line.quantity as number) || 0);
            },
            0
          );

          return [
            {
              title: "Total Lines",
              value: () => totalLines.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Pending",
              value: () => pendingLines.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Scheduled",
              value: () => scheduledLines.toString(),
              valueColor: "success.main",
            },
            {
              title: "Total Quantity",
              value: () => totalQuantity.toString(),
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "production-departments",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
    },
  },
];
